import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, makeStateKey, StateKey, inject } from '@angular/core';

import { Observable, of, catchError, map, pluck, share } from 'rxjs';

import {
  CmsContentType,
  CmsImageData,
  CmsPicture,
  CmsTaxonomy,
  CmsTaxonomyPicture,
  CmsTeaserCard,
} from '@fcom/core-api';
import { finShare, retryWithBackoff } from '@fcom/rx';

import { TripType } from '../../interfaces/triptype.interface';
import { GlobalBookingTravelClass } from '../../interfaces/global-booking-travel-class.interface';
import { getWhitelistedCmsParams } from '../../utils/cms-path-helpers';
import { stringHashCode } from '../../utils/utils';
import { urlToPathname } from '../../utils/node-utils';
import { CampaignTagToLabelKey } from '../../constants';
import { ConfigService } from '../config/config.service';
import { MonitoringLogger } from '../monitoring/monitoring-logger';
import { StateTransferService } from '../state-transfer/state-transfer.service';

export enum I18nKey {
  GLOBAL = 'global',
  BOOKING = 'booking',
  TAX_CODES = 'taxCodes',
  COUNTRY_REGIONS = 'countryRegions',
  MMB = 'mmb',
  REACCOMMODATION = 'reaccommodation',
  LOYALTY = 'loyalty',
  SERVICE_FORMS = 'serviceForms',
  REFUNDS = 'refunds',
  CORPORATE = 'corporate',
  TRAVEL_READY = 'travelReady',
}

export enum I18nLazyLoadingPrefix {
  TAX_CODES = 'TAX_CODES',
  COUNTRY_REGIONS = 'COUNTRY_REGIONS',
  MMB = 'MMB',
  REACCOMMODATION = 'REACCOMMODATION',
  LOYALTY = 'LOYALTY',
  SERVICE_FORMS = 'SERVICE_FORMS',
  REFUNDS = 'REFUNDS',
  CORPORATE = 'CORPORATE',
  TRAVEL_READY = 'TRAVEL_READY',
}

export interface CMSDiscountCode {
  code: string;
  amount: string;
  type: 'fixed' | 'percentage';
}

export interface I18nData {
  key: I18nKey;
  getUrl: (translationsApiUrl: string, lang: string) => string;
  lazyLoadingPrefix?: I18nLazyLoadingPrefix;
}

export const i18nKeyToDataMap: Record<I18nKey, I18nData> = {
  global: {
    key: I18nKey.GLOBAL,
    getUrl: (translationsApiUrl: string, lang: string) => `${translationsApiUrl}/translations/${lang}`,
  },
  booking: {
    key: I18nKey.BOOKING,
    getUrl: (translationsApiUrl: string, lang: string) => `${translationsApiUrl}/translations/${lang}/booking`,
  },
  taxCodes: {
    key: I18nKey.TAX_CODES,
    getUrl: (translationsApiUrl: string, lang: string) =>
      `${translationsApiUrl}/translations/${lang}/booking/tax-codes`,
    lazyLoadingPrefix: I18nLazyLoadingPrefix.TAX_CODES,
  },
  countryRegions: {
    key: I18nKey.COUNTRY_REGIONS,
    getUrl: (translationsApiUrl: string, lang: string) => `${translationsApiUrl}/translations/${lang}/country-regions`,
    lazyLoadingPrefix: I18nLazyLoadingPrefix.COUNTRY_REGIONS,
  },
  mmb: {
    key: I18nKey.MMB,
    getUrl: (translationsApiUrl: string, lang: string) => `${translationsApiUrl}/translations/${lang}/manage-booking`,
    lazyLoadingPrefix: I18nLazyLoadingPrefix.MMB,
  },
  reaccommodation: {
    key: I18nKey.REACCOMMODATION,
    getUrl: (translationsApiUrl: string, lang: string) => `${translationsApiUrl}/translations/${lang}/reaccommodation`,
    lazyLoadingPrefix: I18nLazyLoadingPrefix.REACCOMMODATION,
  },
  loyalty: {
    key: I18nKey.LOYALTY,
    getUrl: (translationsApiUrl: string, lang: string) => `${translationsApiUrl}/translations/${lang}/loyalty`,
    lazyLoadingPrefix: I18nLazyLoadingPrefix.LOYALTY,
  },
  serviceForms: {
    key: I18nKey.SERVICE_FORMS,
    getUrl: (translationsApiUrl: string, lang: string) => `${translationsApiUrl}/translations/${lang}/service-forms`,
    lazyLoadingPrefix: I18nLazyLoadingPrefix.SERVICE_FORMS,
  },
  refunds: {
    key: I18nKey.REFUNDS,
    getUrl: (translationsApiUrl: string, lang: string) => `${translationsApiUrl}/translations/${lang}/refunds`,
    lazyLoadingPrefix: I18nLazyLoadingPrefix.REFUNDS,
  },
  corporate: {
    key: I18nKey.CORPORATE,
    getUrl: (translationsApiUrl: string, lang: string) => `${translationsApiUrl}/translations/${lang}/corporate`,
    lazyLoadingPrefix: I18nLazyLoadingPrefix.CORPORATE,
  },
  travelReady: {
    key: I18nKey.TRAVEL_READY,
    getUrl: (translationsApiUrl: string, lang: string) => `${translationsApiUrl}/translations/${lang}/travel-ready`,
    lazyLoadingPrefix: I18nLazyLoadingPrefix.TRAVEL_READY,
  },
};

export interface CmsSearchParams {
  lang?: string;
  locale: string;
  documentType: string;
  propertySet: string;
  subjectTaxonomyExtRef: string;
  segment?: string;
  transferCache?: boolean;
}

export interface CmsSearchHits<T> {
  hits: T[];
  numHits: number;
}

export interface PromoCodeTermsSearchHit {
  teaserText: string;
  teaserTitle: string;
}

export interface PromoCodeTerms {
  title: string;
  description: string;
}

export interface CampaignSearchHit {
  localSettings: {
    discountCode: CMSDiscountCode;
    selectedTripType: TripType;
    selectedTravelClass: GlobalBookingTravelClass;
    campaignEndText?: string;
    enableBlackTeaser?: boolean;
    restrictToDestinations: {
      destination: string[];
      destinationsByOrigin: Record<string, string[]>;
    };
  };
  subjectTaxonomyTags: CampaignTagToLabelKey[];
}

export interface CmsCampaign {
  destinations?: string[];
  destinationsByOrigin?: Record<string, string[]>;
  campaignEndText: string;
  enableBlackTeaser: boolean;
  campaignLabel?: CampaignTagToLabelKey;
  campaignTravelClass?: GlobalBookingTravelClass;
  discountCode?: CMSDiscountCode;
}

export interface CmsTeaserCardParams {
  lang: string;
  locale: string;
  taxonomyTags: CmsTaxonomy[];
  filter?: string | string[];
  location?: string;
  locations?: string[];
  limit?: number;
  offset?: number;
  softFiltering?: boolean;
}

interface CmsJsonRequestOptions {
  params?: HttpParams;
  transferCache?: boolean;
}

export const stateKey = <T = unknown>(templateUrl: string): StateKey<T> =>
  makeStateKey<T>(`cds-${stringHashCode(templateUrl)}`);

@Injectable()
export class CmsDataService {
  protected http = inject(HttpClient);
  protected config = inject(ConfigService);
  protected stateTransferService = inject(StateTransferService);
  protected monitoringLogger = inject(MonitoringLogger);

  /**
   * Function to fetch JSON content from the CMS server
   *
   * @param url url to send request to
   * @param extraParams object of additional parameters to set on the CMS request.
   * e.g. { tier: 'Basic'}
   */
  getFragmentJson<T>(url: string, extraParams?: Record<string, string>): Observable<T> {
    const params = { ...getWhitelistedCmsParams(url), ...extraParams, view: 'fin-fragment-json' };
    const search = Object.keys(params).reduce((acc, cur) => {
      return acc.set(cur, params[cur]);
    }, new HttpParams());
    const fragmentJsonUrl = `${this.config.cmsUrl}${urlToPathname(url)}`;
    return this.stateTransferService.wrapToStateCache(stateKey<T>(`${fragmentJsonUrl}?${search.toString()}`), () =>
      this.getJson(fragmentJsonUrl, { params: search, transferCache: false })
    );
  }

  // Should not be overridden in sub class
  getLocalization(
    lang: string,
    i18nData: I18nData,
    pluckProperty: string[] = ['localSettings']
  ): Observable<Record<string, unknown>> {
    return this.getPage<Record<string, unknown>>(i18nData, lang).pipe(
      pluck(...pluckProperty),
      map((data: unknown) =>
        i18nData.lazyLoadingPrefix
          ? { [i18nData.lazyLoadingPrefix]: { loaded: true, ...(data as Record<string, unknown>) } }
          : (data as Record<string, unknown>)
      ),
      share()
    );
  }

  getPicturesByTaxonomyTags<
    T extends CmsTaxonomyPicture[],
    R = T extends Array<infer K extends CmsTaxonomyPicture> ? Record<K, CmsPicture | undefined> : never,
  >(taxonomyTags: T, lang: string, locale: string): Observable<R> {
    const prepareResult = (response?: CmsSearchHits<CmsPicture>): R => {
      return taxonomyTags.reduce((previousValue, currentValue) => {
        previousValue[currentValue] = response?.hits?.find((item) => item.subjectTaxonomyTags.includes(currentValue));
        return previousValue;
      }, {} as R);
    };

    return this.search<CmsPicture>({
      lang,
      locale,
      documentType: CmsContentType.CMPicture,
      propertySet: 'finnaircom',
      subjectTaxonomyExtRef: taxonomyTags.map((item) => `Subject/${item}`).join(','),
    }).pipe(
      map((response) => {
        return prepareResult(response);
      }),
      catchError(() => of(prepareResult()))
    );
  }

  // Should not be overridden in sub class
  getBookingPicture(lang: string): Observable<CmsImageData> {
    return this.getPage<{ picture: CmsImageData }>(i18nKeyToDataMap.booking, lang).pipe(pluck('picture'), share());
  }

  getPromoCodeTerms(promoCode: string, lang: string, locale: string): Observable<PromoCodeTerms | undefined> {
    return this.search<PromoCodeTermsSearchHit>({
      lang,
      locale,
      documentType: 'CMArticle',
      propertySet: 'finnaircom',
      subjectTaxonomyExtRef: 'Subject/discountCodeTerms',
      segment: promoCode.toLowerCase(),
    }).pipe(
      map((response) =>
        response?.hits?.length > 0
          ? { title: response.hits[0].teaserTitle, description: response.hits[0].teaserText }
          : undefined
      )
    );
  }

  getOngoingCampaigns(lang: string, locale: string): Observable<CmsCampaign[]> {
    return this.stateTransferService.wrapToStateCache(stateKey<CmsCampaign[]>(`campaigns-${lang}-${locale}`), () =>
      this.search<CampaignSearchHit>({
        lang,
        locale,
        documentType: 'CMPlaceholder',
        propertySet: 'finnaircom',
        subjectTaxonomyExtRef: 'Subject/fcomCampaign',
        transferCache: false,
      }).pipe(
        map((response) => (response?.numHits > 0 ? response.hits : [])),
        map((hits) =>
          hits.map((hit) => ({
            destinations: hit?.localSettings?.restrictToDestinations?.destination,
            destinationsByOrigin: hit?.localSettings?.restrictToDestinations?.destinationsByOrigin,
            campaignEndText: hit?.localSettings?.campaignEndText || '',
            enableBlackTeaser: hit?.localSettings?.enableBlackTeaser || false,
            campaignLabel: CampaignTagToLabelKey[hit?.subjectTaxonomyTags?.[0]],
            campaignTravelClass: hit?.localSettings?.selectedTravelClass || GlobalBookingTravelClass.ECONOMY,
            discountCode: hit?.localSettings?.discountCode,
          }))
        ),
        finShare(),
        catchError(() => of([]))
      )
    );
  }

  /**
   * Builds taxonomy filter query parameters for CMS searches.
   * @param param - Single tag or array of tags to filter by
   * @param softFiltering - If true, uses OR logic (comma-separated values in single parameter).
   *                        If false, uses AND logic (multiple separate parameters)
   * @param subject - If true, builds Subject taxonomy filters. If false, builds Location taxonomy filters
   * @returns URL query string fragment starting with '&' or empty string if no valid filters
   */
  private formatTaxonomyParameter(param: string | string[], softFiltering = false, isSubject = true): string {
    const arr: string[] = !Array.isArray(param) ? [param] : param;
    const tagCategory: 'Subject' | 'Finnair+Location' = isSubject ? 'Subject' : 'Finnair+Location';
    const tagExtRef: 'subjectTaxonomyExtRef' | 'locationTaxonomyExtRef' = isSubject
      ? 'subjectTaxonomyExtRef'
      : 'locationTaxonomyExtRef';
    let filter = '';

    if (softFiltering) {
      const resultArr: string[] = arr
        .map((tag): string | null => (tag ? `${tagCategory}/${tag}` : null))
        .filter(Boolean) as string[];
      filter = resultArr.length ? `&${tagExtRef}=${resultArr.join(',')}` : '';
    } else {
      arr.forEach((tag): void => {
        if (tag) {
          filter += `&${tagExtRef}=${tagCategory}/${tag}`;
        }
      });
    }

    return filter;
  }

  getArticleListByTaxonomyTags(
    params: CmsTeaserCardParams
  ): Observable<{ articles: CmsTeaserCard[]; numHits: number }> {
    const path = `${this.config.cmsUrl}/service/json/v1/search/${params.lang}`;
    const docType = '?documenttype=CMArticle';
    const urlLocale = `&locale=${params.locale}`;
    const baseSubject: string = params.taxonomyTags.map((tag): string => `Subject/${tag}`).join(',');
    const urlLocation: string = params.location ? `&locationTaxonomyExtRef=Finnair+Location/${params.location}` : '';
    const paramLimit: string = params.limit ? `&limit=${params.limit}` : '';
    const paramOffset: string = params.offset ? `&offset=${params.offset}` : '';
    const depthAndProperty = '&nestingDepth=1&propertySet=finnaircom';
    const sorting = '&sortFields=externallydisplayeddate';
    const allowDuplicates = '&duplicates=true';
    const urlFilter: string = params.filter ? this.formatTaxonomyParameter(params.filter, params.softFiltering) : '';
    const urlLocations: string = params.locations
      ? this.formatTaxonomyParameter(params.locations, params.softFiltering, false)
      : '';

    const url = `${path}${docType}${urlLocale}${depthAndProperty}${allowDuplicates}&subjectTaxonomyExtRef=${baseSubject}${urlFilter}${paramLimit}${paramOffset}${sorting}${urlLocation}${urlLocations}`;
    const json$: Observable<CmsSearchHits<CmsTeaserCard>> = this.stateTransferService.wrapToStateCache(
      stateKey<CmsSearchHits<CmsTeaserCard>>(url),
      () => this.getJson<CmsSearchHits<CmsTeaserCard>>(url, { transferCache: false })
    );

    return json$.pipe(
      map((response): { articles: CmsTeaserCard[]; numHits: number } => ({
        articles: response?.numHits > 0 ? response.hits : [],
        numHits: response?.numHits || 0,
      })),
      map((result): { articles: CmsTeaserCard[]; numHits: number } => ({
        articles: result.articles.map((hit): CmsTeaserCard => ({
          url: hit.url,
          teaserTitle: hit.teaserTitle,
          teaserText: hit.teaserText,
          media: hit.media,
          externallyDisplayedDate: hit.externallyDisplayedDate,
          contentId: hit.contentId,
          contentLocale: hit.contentLocale?.replace('_', '-'),
          contentType: CmsContentType.CMTeaser,
          subjectTaxonomyTags: hit.subjectTaxonomyTags,
          viewTypeName: hit.viewType,
        })),
        numHits: result.numHits,
      })),
      finShare(),
      catchError((error: unknown): Observable<{ articles: CmsTeaserCard[]; numHits: number }> => {
        this.monitoringLogger.error('Error fetching filtered articles: ', error);
        return of({ articles: [], numHits: 0 });
      })
    );
  }

  protected search<T>(params: CmsSearchParams): Observable<CmsSearchHits<T>> {
    const urlParamLang = params.lang ? `/${params.lang}` : '';
    const urlParamSegment = params.segment ? `&segment=${params.segment}` : '';

    return this.getJson(
      `${this.config.cmsUrl}/service/json/v1/search${urlParamLang}?documenttype=${params.documentType}&locale=${params.locale}&propertySet=${params.propertySet}&subjectTaxonomyExtRef=${params.subjectTaxonomyExtRef}${urlParamSegment}`,
      { transferCache: params.transferCache }
    );
  }

  protected getPageJson<T>(i18nData: I18nData, lang: string): Observable<T> {
    return this.getJson<T>(i18nData.getUrl(this.config.cfg.translationsApiUrl, lang), { transferCache: false });
  }

  private getPage<T>(i18nData: I18nData, lang: string): Observable<T> {
    const url = i18nData.getUrl(this.config.cfg.translationsApiUrl, lang);
    return this.stateTransferService.wrapToStateCache(stateKey<T>(url), () => this.getPageJson<T>(i18nData, lang));
  }

  /**
   * Pass `transferCache: false` when the response is already cached with StateTransferService,
   * so the payload isn't serialized into the SSR transfer state twice.
   */
  protected getJson<T>(url: string, { params, transferCache = true }: CmsJsonRequestOptions = {}): Observable<T> {
    return this.http
      .get<T>(url, {
        withCredentials: this.config.cfg.enableCorsCredentials,
        params,
        transferCache,
      })
      .pipe(
        retryWithBackoff(1),
        map((data) => {
          // @TODO: Remove delete responsiveImageData when CMS has migrated away from these
          if (data?.['picture']) {
            delete data['picture']['responsiveImageData'];
          }
          if (Array.isArray(data?.['media'])) {
            data['media'].forEach((media) => {
              delete media['responsiveImageData'];
            });
          }
          return data;
        }),
        finShare()
      );
  }
}
